import { useEffect, useState } from 'react';
import { suggestionData } from 'app/modules/components/EntityManager/EntityManagerSearch';
import { useInventorySearchSuggestionQuery } from 'app/modules/inventory/views/Inventory/graphql/queries/generated/inventorySearchSuggestion';
import { InventoryItemSchema, ItemTypeEnum } from 'app/types/schema';
import { standardizeItemSearch, getSearchFieldsForContext, SEARCH_CONFIG } from 'app/utils/itemSearch';

const useSearchSuggestion = (options?: { siteId?: string }) => {
  const { siteId } = options || {};

  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchSuggestions, setSearchSuggestions] = useState<suggestionData[]>();

  const [{ data }] = useInventorySearchSuggestionQuery({
    variables: {
      filters: {
        search: searchedValue,
        siteIds: siteId ? [siteId] : [],
        types: [ItemTypeEnum.Inventory, ItemTypeEnum.InventoryKit],
        includeLocationsInSearch: !!siteId,
      },
      limit: SEARCH_CONFIG.DEFAULT_LIMIT, // Use standardized limit
    },
    pause: searchedValue.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH, // Only search when minimum length is met
  });

  useEffect(() => {
    if (data?.items?.edges) {
      const items = data.items.edges.map(edge => edge.node as InventoryItemSchema);
      const searchResults = standardizeItemSearch(
        items,
        searchedValue,
        getSearchFieldsForContext('inventory')
      );

      const result = searchResults.map(({ label, id, value }) => ({
        label,
        id,
        value,
      }));

      setSearchSuggestions(result);
    } else {
      setSearchSuggestions([]);
    }
  }, [data, searchedValue]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchedValue(searchValue);
    }, SEARCH_CONFIG.DEBOUNCE_DELAY);
    return () => clearTimeout(delayDebounceFn);
  }, [searchValue]);

  return {
    searchSuggestions,
    setSearchValue,
  };
};

export type UseSearchSuggestionReturnType = ReturnType<typeof useSearchSuggestion>;

export default useSearchSuggestion;
